// Feature-flag gate for the Beta selectors — one PostHog flag per product
// (user decision 2026-07-11: independent flag + override per product, so each
// surface can roll out, A/B, or roll back on its own).
// Variant values: 'beta' | 'classic'. Anything else — flag off, SDK blocked,
// flags not loaded within the timeout — resolves to 'classic' (the hard default).
import posthog from 'posthog-js'; // singleton — same instance main.js initialized

export const SELECTOR_PRODUCTS = {
  ifp:              { flag: 'new-selector-ifp',              override: 'selector_ui_override_ifp' },
  imp:              { flag: 'new-selector-imp',              override: 'selector_ui_override_imp' },
  company_profile:  { flag: 'new-selector-company-profile',  override: 'selector_ui_override_company_profile' },
  prospect_list:    { flag: 'new-selector-prospect-list',    override: 'selector_ui_override_prospect_list' },
  financial_report: { flag: 'new-selector-financial-report', override: 'selector_ui_override_financial_report' },
  market_report:    { flag: 'new-selector-market-report',    override: 'selector_ui_override_market_report' },
  valuation_report: { flag: 'new-selector-valuation-report', override: 'selector_ui_override_valuation_report' },
  company_report:   { flag: 'new-selector-company-report',   override: 'selector_ui_override_company_report' },
};

export function resolveSelectorVariant(productKey, timeoutMs = 1500) {
  const cfg = SELECTOR_PRODUCTS[productKey];
  return new Promise((resolve) => {
    let settled = false;
    const finish = (v) => {
      if (settled) return;
      settled = true;
      resolve(v === 'beta' ? 'beta' : 'classic');
    };
    if (!cfg) return finish('classic');
    try {
      const current = posthog.getFeatureFlag(cfg.flag);
      // undefined = flags not evaluated yet this session; returning users hit
      // posthog-js's localStorage cache and resolve synchronously here.
      if (current !== undefined) return finish(current);
      posthog.onFeatureFlags(() => finish(posthog.getFeatureFlag(cfg.flag)));
      setTimeout(() => finish('classic'), timeoutMs);
    } catch (e) {
      finish('classic');
    }
  });
}

// The user's explicit Beta/classic choice for ONE product surface. Stored as a
// person property so it follows the account across devices; the flag's release
// conditions rank it above the experiment bucket. Takes effect on next visit.
// Resolves once the flag actually serves the requested variant (or after a
// 4s timeout). Callers reload the page AFTER this resolves — reloading earlier
// makes the gate read posthog's stale cached value and the switch silently
// misses a visit. setPersonPropertiesForFlags sends the property WITH the flag
// request (no ingestion lag) and persists it locally; the $set on
// setPersonProperties handles cross-device durability.
export function setSelectorOverride(productKey, to) {
  const cfg = SELECTOR_PRODUCTS[productKey];
  if (!cfg) return Promise.resolve();
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    try {
      posthog.capture('selector.variant_switched', { to, product: productKey });
      posthog.setPersonProperties({ [cfg.override]: to });
      if (typeof posthog.setPersonPropertiesForFlags === 'function') {
        // store only; we trigger the reload ourselves below
        posthog.setPersonPropertiesForFlags({ [cfg.override]: to }, false);
      }
      posthog.reloadFeatureFlags();
      const t0 = Date.now();
      const iv = setInterval(() => {
        let v;
        try {
          v = posthog.getFeatureFlag(cfg.flag);
        } catch (e) {
          v = undefined;
        }
        if (v === to || Date.now() - t0 > 4000) {
          clearInterval(iv);
          finish();
        }
      }, 150);
    } catch (e) {
      finish();
    }
  });
}
